import { Injectable } from '@angular/core';
import { QueryEntity } from '@datorama/akita';
import { compareEntities, compareEntity } from '@lycoris-nubila/ngx-lycoris';
import { isNil, reject, uniqBy } from 'lodash-es';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';

import { JobOffer } from 'job/job-offer';
import { JobOfferState, JobOfferStore } from 'job/job.store';
import { JobOfferRepositoryPort } from 'job/port/job-offer-repository.port';
import { ObserveJobOffersByOwnerIdAndOrganizationModuleIdQuery } from 'job/query/observe-job-offers-by-owner-id-and-organization-module-id.query';

import { CacheObservable } from '../../app-decorators';

@Injectable()
export class JobOfferRepositoryAdapter
	extends QueryEntity<JobOfferState>
	implements JobOfferRepositoryPort
{
	constructor(protected store: JobOfferStore) {
		super(store);
	}

	public observeJobOfferById(id: string): Observable<JobOffer> {
		return this.selectEntity(id).pipe(
			map((document) => this.store.asEntity(document)),
			distinctUntilChanged(compareEntity),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	@CacheObservable()
	public observeJobOffersByIds(ids: string[]): Observable<JobOffer[]> {
		return this.selectMany(ids).pipe(
			map((documents) =>
				documents.map((document) => this.store.asEntity(document))
			),
			distinctUntilChanged(compareEntities),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	@CacheObservable()
	public observeJobOffersByOwnerIdAndOrganizationModuleId(
		query: ObserveJobOffersByOwnerIdAndOrganizationModuleIdQuery
	): Observable<JobOffer[]> {
		return this.selectAll({
			filterBy: (entity) =>
				entity.modulesColumn.some(
					(mc) => mc.organizationModuleId === query.organizationModuleId
				) && query.ownerId === entity.ownerId,
		}).pipe(
			map((documents) =>
				documents.map((document) => this.store.asEntity(document))
			),
			distinctUntilChanged(compareEntities),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	public async saveJobOffers(entities: JobOffer[]): Promise<void> {
		this.store.upsertMany(
			uniqBy(
				reject(entities, isNil).map((e) => this.store.asDocument(e)),
				'id'
			)
		);
	}
}
